export type CyberBytesId = number

export interface CreatedBy {
  id: CyberBytesId | string
  email: string
  display_name: string
  avatar_initials?: string
  created_at?: string
}

export interface Series {
  id: CyberBytesId
  title: string
  description: string
  category: string
  tags: string[]
  created_by: CreatedBy
  published: boolean
  created_at: string
  updated_at?: string
  modules_count: number
  viewers_count: number
  completed_count: number
  average_rating: number
  ratings_count: number
}

export type ModuleType =
  | 'text'
  | 'image'
  | 'youtube'
  | 'vidcast'
  | 'mindtickle'
  | 'moodle'
  | 'journey_map'

export interface Module {
  id: CyberBytesId
  series?: CyberBytesId
  title: string
  description?: string
  type: ModuleType
  content: any
  order_index: number
  required: boolean
  section?: CyberBytesId | null
  created_at?: string
  updated_at?: string
}

export interface Section {
  id: CyberBytesId
  series?: CyberBytesId
  title: string
  description?: string
  order_index: number
  modules: Module[]
  created_at?: string
  updated_at?: string
}

export interface ModuleProgressEntry {
  id: CyberBytesId
  viewed: boolean
  completed: boolean
}

export interface UserProgress {
  overallProgress: number
  modules: ModuleProgressEntry[]
}

export interface SeriesRating {
  average: number
  count: number
  // Older payloads returned just the number.
  userRating: number | { rating: number; review?: string } | null
}

export interface Review {
  id: CyberBytesId
  rating: number
  review?: string
  user?: CyberBytesId | { id: CyberBytesId | string; display_name?: string }
  created_at?: string
}

export interface SeriesDetail {
  series: Series
  modules: Module[]
  sections: Section[]
  userProgress: UserProgress
  rating: SeriesRating
  reviews: Review[]
}

export const CATEGORIES = [
  { id: 'security', label: 'Security' },
  { id: 'networking', label: 'Networking' },
  { id: 'cloud', label: 'Cloud' },
  { id: 'data', label: 'Data Science' },
  { id: 'development', label: 'Development' },
]

export const MODULE_TYPES: { id: ModuleType; label: string }[] = [
  { id: 'text', label: 'Text' },
  { id: 'image', label: 'Image' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'vidcast', label: 'Vidcast' },
  { id: 'mindtickle', label: 'Mindtickle' },
  { id: 'moodle', label: 'Moodle' },
  { id: 'journey_map', label: 'Journey Map' },
]

// Legacy modules have no `format`; TextContentView auto-detects HTML for them.
export type TextFormat = 'plain' | 'html' | 'markdown'

export const TEXT_FORMATS: { id: TextFormat; label: string }[] = [
  { id: 'plain', label: 'Plain Text' },
  { id: 'html', label: 'HTML' },
  { id: 'markdown', label: 'Markdown' },
]

export const TEXT_FORMAT_TEMPLATES: Record<TextFormat, string> = {
  plain: '',
  html: `<h2>Heading</h2>\n<p>Start writing your content here. You can use <strong>bold</strong>, <em>italic</em>, and <a href="https://example.com">links</a>.</p>\n<ul>\n  <li>First point</li>\n  <li>Second point</li>\n</ul>`,
  markdown: `## Heading\n\nStart writing your content here. You can use **bold**, *italic*, and [links](https://example.com).\n\n- First point\n- Second point`,
}
